/**
 * Where a deleted entity LIVED in Firestore, and whether it is back (ATL-1207).
 *
 * WHY THIS EXISTS. A dual-write delete that fails is queued and replayed later — for up to seven
 * days by the durable outbox (ATL-782), or until the policy heals by the in-session pending-skip
 * queue (ATL-1192). Both queues decide whether a queued delete may still fire from state observed
 * IN THIS PROCESS: the outbox purges it when a live write for the document lands on THIS device
 * (`noteDualWriteSuccess`), the pending-skip queue when one lands in THIS session
 * (`purgePendingSkipsForDocument`). A recreate performed on ANOTHER till — or in another browser
 * tab — is invisible to both, so the replay fires and removes a row the other device just created.
 * Firestore keeps it, Supabase does not, and the delete reports success.
 *
 * WHY FIRESTORE IS THE ORACLE — and why ATL-1207's own filing was wrong about this. The ticket
 * concluded "a client-side guard cannot close it: the whole point is that the competing write
 * happened somewhere this client cannot observe", and proposed an `If-Unmodified-Since`-style
 * precondition on all 39 delete routes instead. The premise does not hold: the competing write
 * happened in FIRESTORE, which this client observes as a matter of course — every dual-write
 * commits there FIRST, in both read modes, so it is the store both tills converge on. What the
 * client cannot observe is SUPABASE's copy, and that is not the question.
 *
 * The backend genuinely cannot answer it: it holds no record of a delete that never reached it,
 * and comparing its row's `created_at` against the entry's `enqueuedAt` would compare a server
 * clock with a POS tablet's. So the seam that CAN answer is this one.
 *
 * ⚠️ Phase 6 (removing Firestore writes) is where this stops being true. At that point the shared
 * store is Supabase and this module needs a different source, not a wider one — a delete's
 * precondition read from the store the delete itself targets is not a precondition at all.
 *
 * WHY THE READ IS `source: 'server'`. A cached read answers with what THIS device already knew,
 * which is precisely the state that is wrong — and on native the SDK's default IS the cache
 * (persistence is on), so `get()` would have quietly re-asked the device that already believes the
 * entity is gone. When the server cannot be reached the read THROWS, which is the honest answer
 * ("unknown") and the one the callers hold on.
 *
 * WHERE IT IS WIRED, and why not everywhere. A stale delete can only destroy a recreated row where
 * a document id can be REUSED. The constants families are exactly that population: their ids are
 * typed by the user, and `checkIdUniquenes` only rejects an id currently present, so the id of a
 * deleted item is free again the moment it is deleted. Every other family takes a Firestore-minted
 * id (`collection(...).doc()`), so a queued delete there names an id nothing will ever hold again
 * and the probe would be a network read that can only ever answer "still deleted". Those entries
 * carry no location and are replayed exactly as before. (The user disable/enable legs carry one
 * too, for a different reason — a field-state race, not a reused id; see THE SECOND SHAPE below.)
 *
 * The `./types` require is LAZY (and so is this module's own require at the drain): `./types`
 * reaches the Jotai atoms and, through them, the app graph that imports the callers — the same
 * cycle `dualWriteOutbox` documents on its own requires. `isDeletedEntityLocation` above stays
 * pure and importable, so the enqueue seam can validate a location without any of that.
 *
 * WHAT IT COSTS. One `get` per QUEUED ENTRY, not per document — a manager clearing fifteen payment
 * methods during an outage queues fifteen entries that each read `constants/paymentMethods`, on
 * every drain pass, until they resolve. Deliberately not memoised per pass: a pass can span
 * minutes, and answering a later entry from a snapshot taken before a recreate is exactly the harm
 * this module exists to prevent, so a memo would need a TTL short enough to be nearly pointless.
 * The read is small and the population is bounded by the queue caps (100 durable entries, 200
 * in-session); if it ever shows up in Firestore read volume, the fix is a TTL'd memo keyed on
 * `collection/document`, not the removal of the check.
 *
 * WHAT A WITHDRAWAL LEAVES BEHIND, named rather than implied. The delete is abandoned, so the
 * Supabase row that survives is the one the delete never removed — same `firestore_id`, but the
 * FIELD VALUES the entity had before it was deleted, while Firestore holds the ones the other
 * device typed when it re-added it. That is fidelity drift, repaired by the next update of that
 * document or by the fidelity sweep, and it is the deliberate trade: a retained row is detectable
 * by the id-level set-diff and the census, a destroyed one leaves no trace on either side. (The
 * backend's own half of this — a create whose row is SOFT-DELETED returns the deleted row
 * unchanged, so a recreate after a landed delete is lost — is ATL-1210, and is not reachable from
 * the path this module guards, where the delete by construction never landed.)
 *
 * THE SECOND SHAPE — A FIELD'S STATE (ATL-1209). `users.softDelete` does not remove anything: it
 * sets `active: false` on `users/<id>` (the Cloud Function `disableUser`), and `users.enable` sets
 * it back, from any device. "Is the entity present" is the wrong predicate there; the right one is
 * the FIELD's value, so a location may name `field` + `expected` instead: the queued write still
 * applies while `field` holds `expected`, and any other value means another device changed it since
 * (`recreated` — withdrawn, exactly like a re-added constant). A replayed disable over a re-enable
 * would otherwise deactivate the Supabase row, and `FirebaseAuthGuard` 401s an inactive user — on a
 * Supabase-read organization that locks the cashier out, and on every organization it silently
 * parks that device's own dual-writes behind an auth hold (ATL-908). The mirror image — a replayed
 * enable over a later disable — is the same race on the same field, so it carries the same guard.
 *
 * `users` is a ROOT collection (the Cloud Functions write `db.collection('users')`), not an
 * organization subcollection, and its documents are keyed by the Firestore document id while the
 * API — and so the queued entry's id — is keyed by the auth uid. So a location may say `root`, must
 * then name its `document` explicitly, and names an `identityField` the document must carry the
 * entry's id in. A root document is outside the organization handle's own scoping, so the org
 * check moves onto the document: it must say `organizationId` = the queued write's org, or the
 * answer is `unknown`.
 */
// A leaf module with no imports of its own, so a static import reopens none of the cycles the
// lazy requires below avoid.
import { withTimeout } from '../../utils/withTimeout'

/**
 * How long a probe may take before it answers `unknown`.
 *
 * The read is on the replay path of both queues, and both hold a flag for its duration — the
 * outbox's `drainInFlight`, the pending-skip queue's `pendingSkipDrainRunning`. A Firestore `get`
 * has no timeout of its own, so an unbounded one would not merely delay a delete: it would wedge
 * the whole queue for the rest of the session, which is the failure this file exists to avoid,
 * arrived at from the other side. 10s is well above a healthy server read and well below the 60s
 * write timeout the replay itself gets.
 */
const PROBE_TIMEOUT_MS = 10_000

export interface DeletedEntityLocation {
  /**
   * Collection that held the entity: under the ORGANIZATION document (`constants`, `stores`, …),
   * or at the database root when `root` is set (`users`).
   */
  collection: string
  /**
   * The collection is at the DATABASE ROOT. Requires `document` (a root document is never the
   * entry's own id here), `identityField` and `field`; the document must name this organization.
   */
  root?: boolean
  /**
   * The document field that must equal the queued entry's id — how a root document is tied to the
   * entity the API names (`uid` for `users`). Meaningful only with `root`.
   */
  identityField?: string
  /**
   * FIELD-STATE shape: the queued write applies only while this field of the document holds
   * `expected` (`active` for a user). Exclusive with `arrayField`.
   */
  field?: string
  /** The value `field` must still hold for the queued write to fire. Required with `field`. */
  expected?: boolean | string | number | null
  /**
   * The document within that collection, when the entity is an ITEM of an array field on it —
   * the constants families, which store every wallet / payment method / role as an element of
   * `constants/<name>.data`. Absent means the entity IS a document, keyed by its own id.
   */
  document?: string
  /** The array field holding those items (`data`). Meaningless without `document`. */
  arrayField?: string
}

/**
 * The organization-scoped Firestore handle (`organizations/<orgFirestoreId>`), as the two SDKs'
 * shared namespaced surface — the only part of it this module needs.
 */
type OrganizationDb = {
  /**
   * The organization document's OWN id, on both SDKs (`DocumentReference.id`). Read so the org
   * check below can be made against the handle the read actually goes through — see there.
   * Optional because a test double need not carry one.
   */
  id?: unknown
  collection: (name: string) => {
    doc: (id: string) => { get: (options: { source: string }) => Promise<unknown> }
    /**
     * Used only to resolve a ROOT document by its identity field (`users` where `uid == …`).
     * Optional because a test double need not carry one; without it that step answers `unknown`.
     */
    where?: (
      field: string,
      op: '==',
      value: string,
    ) => { limit: (n: number) => { get: (options: { source: string }) => Promise<unknown> } }
  }
}

/** A snapshot's fields, or undefined when it has none to read. */
function snapshotFields(snapshot: unknown): Record<string, unknown> | undefined {
  const data = (snapshot as { data?: unknown } | null | undefined)?.data
  if (typeof data !== 'function') return undefined
  const fields = (data as () => unknown).call(snapshot)
  return fields && typeof fields === 'object' ? (fields as Record<string, unknown>) : undefined
}

/**
 * `read`, started only if the probe's cap has time left and raced against what is left of it.
 * Rejects on a spent cap or a timeout; every caller is inside a `catch` that answers `unknown`.
 * A spent cap issues NO read: a server round-trip whose answer cannot be waited for is a wasted
 * network read on an offline till. See the first read in `probeDeletedEntity` for why an abandoned
 * read needs no rejection handler of its own.
 */
function withinProbeCap<S>(read: () => Promise<S>, capMs: number): Promise<S> {
  if (capMs <= 0) return Promise.reject(new Error('deleted-entity probe: cap spent before the read'))
  return withTimeout(read(), capMs, () => new Error('deleted-entity probe: read timed out'))
}

/**
 * The fields of the ROOT document the queued write's entity lives in, resolved by the Cloud
 * Function's own rule (`resolveUserDocRef` in `apps/functions/userAdminAuthz.js`): the stated
 * document when it belongs to the entity, else the document keyed by the entity's id when it does,
 * else the ONE document recording that identity — two is ambiguity, never a guess. A recorded
 * identity decides; only a document without one is identified by its id.
 *
 * Every step is needed. The stated document is what the screen passed, which in Supabase-read mode
 * is the API's id (the auth uid, `users.firestore_id`) rather than a legacy document's own id — so
 * a probe that read only the stated document would answer `unknown` forever for exactly the users
 * the Cloud Function resolves by its fallback, and their queued write would be held until it
 * expired instead of replaying.
 */
async function resolveRootDocumentFields(
  db: OrganizationDb,
  location: DeletedEntityLocation,
  documentId: string,
  stated: unknown,
  exists: (snapshot: { exists?: unknown } | null | undefined) => boolean,
  remainingMs: () => number,
): Promise<Record<string, unknown> | 'unknown'> {
  const identityField = location.identityField
  // `isDeletedEntityLocation` already requires it for a root location; narrowed, not asserted.
  if (identityField === undefined) return 'unknown'
  const belonging = (snapshot: unknown, id: string | undefined): Record<string, unknown> | undefined => {
    if (!exists(snapshot as { exists?: unknown })) return undefined
    const fields = snapshotFields(snapshot)
    if (!fields) return undefined
    const identity = fields[identityField]
    return identity === documentId || (identity === undefined && id === documentId) ? fields : undefined
  }

  const statedFields = belonging(stated, location.document)
  if (statedFields) return statedFields
  try {
    const collection = db.collection(location.collection)
    if (location.document !== documentId) {
      const own = await withinProbeCap(() => collection.doc(documentId).get({ source: 'server' }), remainingMs())
      const ownFields = belonging(own, documentId)
      if (ownFields) return ownFields
    }
    if (typeof collection.where !== 'function') return 'unknown'
    // Building the query issues nothing; only its `get` is a read.
    const byIdentity = collection.where(identityField, '==', documentId).limit(2)
    const query = await withinProbeCap(() => byIdentity.get({ source: 'server' }), remainingMs())
    const docs = (query as { docs?: unknown } | null | undefined)?.docs
    if (!Array.isArray(docs) || docs.length !== 1) return 'unknown'
    return snapshotFields(docs[0]) ?? 'unknown'
  } catch {
    // Offline, permission-denied: the question was not answered.
    return 'unknown'
  }
}

/**
 * `recreated`    — the entity is present again: the queued delete is stale and must not fire.
 * `still-deleted`— Firestore agrees the entity is gone: replay.
 * `unknown`      — nothing could be established. Callers HOLD; they never replay on this.
 */
export type DeletedEntityVerdict = 'recreated' | 'still-deleted' | 'unknown'

/** The location of an item inside a constants document. */
export function constantsItemLocation(document: string): DeletedEntityLocation {
  return { collection: 'constants', document, arrayField: 'data' }
}

/**
 * The precondition of a queued `users.softDelete` (`active` still `false`) or `users.enable`
 * (`active` still `true`) on the root `users/<documentId>` document, tied to the auth uid the API
 * is keyed on (ATL-1209).
 */
export function userActiveLocation(documentId: string, active: boolean): DeletedEntityLocation {
  return {
    collection: 'users',
    root: true,
    document: documentId,
    identityField: 'uid',
    field: 'active',
    expected: active,
  }
}

const EXPECTED_TYPES = new Set(['boolean', 'string', 'number'])

/**
 * Is this a location this module can actually read? Every shape is total: a document location
 * needs a collection, an array-item location needs a document to read the array off, a field-state
 * location needs the value it compares against, and a root location needs the document and the
 * field that ties it to the entry. Anything short of that names no readable location at all, and
 * answering it would mean guessing — so it is `unknown`, not "absent".
 */
export function isDeletedEntityLocation(value: unknown): value is DeletedEntityLocation {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (typeof v.collection !== 'string' || v.collection.length === 0) return false
  if (v.document !== undefined && (typeof v.document !== 'string' || v.document.length === 0)) return false
  if (v.arrayField !== undefined && (typeof v.arrayField !== 'string' || v.arrayField.length === 0)) return false
  if (v.arrayField !== undefined && v.document === undefined) return false
  if (v.root !== undefined && v.root !== true) return false
  if (v.identityField !== undefined && (typeof v.identityField !== 'string' || v.identityField.length === 0)) return false
  // A root document is never addressed by the entry's own id, and is tied to it only by a field.
  // It must also name a FIELD: a root document that is gone cannot show which organization it
  // belonged to, so a presence-only root location could answer `recreated` or `unknown` but never
  // `still-deleted` — a precondition that can only ever withdraw or hold its write.
  if (
    v.root === true &&
    (v.document === undefined || v.identityField === undefined || v.field === undefined)
  ) {
    return false
  }
  if (v.identityField !== undefined && v.root !== true) return false
  if (v.field !== undefined) {
    if (typeof v.field !== 'string' || v.field.length === 0) return false
    if (v.arrayField !== undefined) return false
    if (!('expected' in v)) return false
    if (v.expected !== null && !EXPECTED_TYPES.has(typeof v.expected)) return false
  } else if ('expected' in v) {
    return false
  }
  return true
}

/**
 * Has the entity at `location` with id `documentId` come back since it was deleted?
 *
 * `orgFirestoreId` is the org the QUEUED WRITE belongs to and is checked against the session's
 * current org before anything is read. A probe run against another org's data would compare the
 * wrong `constants/wallets` and could answer "still deleted" about a document it never looked at —
 * the failure mode that ends in a destroyed row, so it is refused before the read rather than
 * after it. (The outbox drain already skips foreign-org entries; this does not rely on that.)
 *
 * Every failure resolves to `unknown`. This function never throws: it is called on the replay path
 * of a module whose whole contract is that a dual-write failure is not fatal.
 */
export async function probeDeletedEntity(
  location: DeletedEntityLocation | undefined,
  documentId: string,
  orgFirestoreId: string,
): Promise<DeletedEntityVerdict> {
  if (!isDeletedEntityLocation(location)) return 'unknown'
  if (typeof documentId !== 'string' || documentId.length === 0) return 'unknown'

  let db: OrganizationDb | null = null
  /**
   * The platform-safe existence check (`types.snapshotExists`): the two SDKs disagree about
   * whether `exists` is a method or a property, and reading it directly is the ATL-570 defect that
   * broke every Firestore read on web. Taken from `./types` rather than re-implemented so there is
   * one answer to that question in the app.
   */
  let exists: ((snapshot: { exists?: unknown } | null | undefined) => boolean) | null = null
  try {
    // Lazily required for the same cycle reason as the outbox's own requires: `./types` reaches
    // the Jotai atoms, and through them the app graph that imports this module's callers.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const types = require('./types') as {
      getDb: () => unknown
      getOrganization: () => { id?: string } | null
      snapshotExists: (snapshot: { exists?: unknown } | null | undefined) => boolean
    }
    if (types.getOrganization()?.id !== orgFirestoreId) return 'unknown'
    if (location.root) {
      // Lazy for the same reason; the root handle, because `users` is not under the organization.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      db = (require('./firebaseFirestore') as { rootDb?: unknown }).rootDb as OrganizationDb | null
    } else {
      db = types.getDb() as OrganizationDb | null
    }
    exists = types.snapshotExists
  } catch {
    return 'unknown'
  }
  if (!db || typeof db.collection !== 'function' || typeof exists !== 'function') return 'unknown'
  // …and the HANDLE names that org too. `getOrganization()` and `getDb()` are two SEPARATE Jotai
  // atoms — `organizationAtom` is populated by the org snapshot listener, `dbAtom` is pointed at
  // `organizations/<user.organizationId>` at auth — so checking one and reading through the other
  // establishes the refusal about a source the read does not use. If they ever disagree (a
  // re-login into a different org that repoints one before the other), the check above passes
  // while this read answers about ANOTHER org's `constants/<family>`, and `still-deleted` is the
  // verdict that FIRES the delete. Asserted on the object that is read, not on a second source
  // expected to agree with it. A handle with no `id` (a test double) falls through to the check
  // above rather than refusing.
  if (!location.root && typeof db.id === 'string' && db.id !== orgFirestoreId) return 'unknown'

  // ONE cap for the whole probe, however many reads it takes (a root location may need three).
  const deadline = Date.now() + PROBE_TIMEOUT_MS
  const remainingMs = () => Math.max(0, deadline - Date.now())

  let snapshot: { data?: () => unknown } | undefined
  try {
    const documentName = location.document ?? documentId
    // A read the cap abandons needs NO rejection handler of its own, and a review round asserted
    // the opposite before a negative control settled it: `Promise.race` subscribes to every input,
    // so a rejection arriving after the race has settled is swallowed rather than left unhandled.
    // (The pending-skip drain's capped REPLAY does keep a handle on its abandoned promise — for a
    // different reason: it still has something to report and something to release.)
    const handle = db
    const answer = await withinProbeCap(
      () => handle.collection(location.collection).doc(documentName).get({ source: 'server' }),
      remainingMs(),
    )
    snapshot = answer as { data?: () => unknown }
  } catch {
    // Offline, permission-denied, an SDK that cannot serve a server read, a read that outran the
    // cap — all of them mean the question was not answered. Never "absent": that is the answer
    // that fires the delete. (A read that outran the cap is abandoned, not cancelled — and
    // abandoning it is safe here in a way it is not for a replay: this is a READ, so nothing
    // landed and no ordering was disturbed.)
    return 'unknown'
  }

  // A FIELD-STATE location — every root location is one (`isDeletedEntityLocation` requires it).
  if (location.field !== undefined) {
    // Neither shape can say anything about a document it cannot read: a field has no state there,
    // and a root document that is gone cannot show which organization it belonged to.
    let fields: Record<string, unknown> | undefined
    if (location.root) {
      // The document that is the entity the queued write names — resolved as the Cloud Function
      // resolves it, not only by the id the screen passed.
      const resolved = await resolveRootDocumentFields(db, location, documentId, snapshot, exists, remainingMs)
      if (resolved === 'unknown') return 'unknown'
      fields = resolved
      // The org check the organization handle gave the other shapes, made on the document itself.
      if (fields.organizationId !== orgFirestoreId) return 'unknown'
    } else {
      if (!exists(snapshot as { exists?: unknown })) return 'unknown'
      fields = snapshotFields(snapshot)
      if (!fields) return 'unknown'
    }
    const value = fields[location.field]
    if (value === location.expected) return 'still-deleted'
    // An absent field is not a changed one: nothing established that another device acted.
    return value === undefined ? 'unknown' : 'recreated'
  }
  const documentExists = exists(snapshot as { exists?: unknown })
  if (!location.arrayField) return documentExists ? 'recreated' : 'still-deleted'
  // The document that HOLDS the items is gone, so the item is gone with it.
  if (!documentExists) return 'still-deleted'
  const items = snapshotFields(snapshot)?.[location.arrayField]
  // A field that is not an array is a shape this module does not understand. Reporting "absent"
  // for it would fire the delete on the strength of something never read.
  if (!Array.isArray(items)) return 'unknown'
  return items.some(
    (item) => item && typeof item === 'object' && (item as { id?: unknown }).id === documentId,
  )
    ? 'recreated'
    : 'still-deleted'
}
